<template>
  <div>
    <!-- Organizations table -->
    <div class="card" qid="organizations-section" id="update_organization">
      <div class="card-body" :class="{'pr-0 pl-0': showOnTask}">
        <div class="row">
          <div class="col-sm-6">
            <h5 qid="organizations-table-title" class="mb-2" v-if="!showOnTask">
              {{ $t('activities.organizations_label') }}
            </h5>

            <span v-if="!showOnTask">
              <span v-if="getTask('update_organization')"
                    class="cursor-pointer task-label-open mr-2"
              >
                <UpdateSingleTaskModal
                    :task="getTask('update_organization')"
                    @reloadContent="reloadContent"
                    :editIcon="true"
                ></UpdateSingleTaskModal>
              </span>
              <span v-if="getGuide('update_organization')">
                <GuideButton :guide="getGuide('update_organization')"></GuideButton>
              </span>
            </span>
          </div>
          <div class="col-sm-6 text-right">
            <span class="btn btn-success"
                    @click="showCreateOrganizationModal"
                    qid="organizations-create-button"
                    v-if="isGranted([permissions.ACTIVITY_ORGANIZATION_CREATE, permissions.ACTIVITY_ORGANIZATION_CREATE_OWN], activity)"
            >
              <font-awesome-icon icon="plus"/> {{ $t('activities.create.organizations_add_new') }}
            </span>
          </div>
        </div>

        <table class="table table-responsive-md table-list-secondary">
          <thead>
            <tr>
              <th scope="col">
                {{ $t('activities.organizations.table.name') }}
              </th>
              <th scope="col" class="text-center">
                {{ $t('activities.organizations.table.level') }}
              </th>
              <th scope="col">
                {{ $t('activities.organizations.table.linked_organization') }}
              </th>
              <th scope="col">
                {{ $t('system.last_updated') }}
              </th>
              <th width="130"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="object in organizationTableItems">
              <td scope="row">
                <span v-if="object.organization && object.organization.name"
                      @click="openViewOrganizationModal(object)"
                      class="btn btn-link cursor-pointer text-left"
                >
                  {{ object.organization.name }}
                </span>
              </td>
              <td class="text-center">
                <span class="badge" v-if="object.organization && object.organization.organization_level">
                  {{ $t('activities.create.organizations_level') }} {{ object.organization.organization_level }}
                </span>
              </td>
              <td>
                <span v-if="object.organization && object.organization.linkedOrganization" :title="object.organization.linkedOrganization.name" class="badge">
                  <font-awesome-icon icon="sitemap"/>
                  {{ object.organization.linkedOrganization.name | truncate(35) }}
                </span>
              </td>
              <td>
                <span class="text-normal">
                  {{ object.updated_at | formatDate }}
                </span>
              </td>
              <td align="right">
                <span v-if="object.organization && object.organization.name"
                      @click="openViewOrganizationModal(object)"
                      class="btn btn-view"
                >
                  <font-awesome-icon icon="eye"/>
                </span>
                <span class="btn btn-delete"
                      qid="delete-organization-modal"
                      v-if="isGranted([permissions.ACTIVITY_ORGANIZATION_DELETE, permissions.ACTIVITY_ORGANIZATION_DELETE_OWN], activity)"
                      @click="showOrganizationConfirmDeleteModal(object)"
                >
                  <font-awesome-icon icon="trash-alt" />
                </span>
              </td>
            </tr>
            <tr v-if="!organizationLoading && !organizationTableItems.length"
                qid="no-results-row-organizations"
                class="text-center"
            >
              <td colspan="10">
                <h5 class="mt-3">
                  {{ $t('system.no_results') }}
                </h5>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>


    <!-- Delete organization modal template -->
    <b-modal ref="delete-organization" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-org-title">
          {{ $t('activities.organizations.delete.title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('activities.organizations.delete.description') }}
        <span v-if="itemToDelete.organization">{{ itemToDelete.organization.name }}</span>
      </h5>
      <h5 class="text-center">
        {{ $t('activities.organizations.delete.are_you_sure') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="hideOrganizationConfirmDeleteModal"
                  qid="delete-org-cancel-button"
        >
          {{ $t('activities.organizations.delete.cancel') }}
        </b-button>
        <b-button @click="confirmDeleteOrganization(itemToDelete)"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-org-submit-button"
                  v-if="isGranted([permissions.ACTIVITY_ORGANIZATION_DELETE, permissions.ACTIVITY_ORGANIZATION_DELETE_OWN], activity)"
        >
          {{ $t('activities.organizations.delete.submit') }}
        </b-button>
      </div>
    </b-modal>


    <!-- Add new organization -->
    <b-modal ref="add-organization" hide-footer @hide="hideCreateOrganizationModal">
      <div class="d-block text-center">
        <h4 qid="add-io-title">
          {{ $t('activities.organizations.create.title') }}
        </h4>
      </div>
      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="create-io-error"
      >
        {{ error }}
      </b-alert>
      <b-form @submit="organizationFormSubmit" qid="create-org-form">
        <div class="form-container">
          <b-form-group>
            <label qid="create-org-org-label">
              {{ $t('activities.organizations.create.org_label') }}
            </label>
            <multiselect
                v-model="selectedOrg"
                :options="orgItems"
                :placeholder="$t('activities.organizations.create.org_placeholder')"
                label="name"
                @search-change="loadOrg"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                track-by="slug"
                qid="create-lb-form-org"
            ></multiselect>
          </b-form-group>
          <div v-if="selectedOrg && selectedOrg.description"
               class="text-center mb-3 mt-4"
          >
            <span class="cursor-pointer">
              <span class="mr-2"
                    @click="showOrganizationDescription = !showOrganizationDescription"
                    qid="expand-organization-description"
              >
                {{$t('activities.organizations.create.org_description_expand')}}
              </span>
              <font-awesome-icon icon="chevron-down" v-if="!showOrganizationDescription"/>
              <font-awesome-icon icon="chevron-up" v-if="showOrganizationDescription"/>
            </span>
          </div>
          <div v-if="selectedOrg && selectedOrg.description && showOrganizationDescription" class="mb-4 mt-2">
            <span class="text-formatted">
              {{ selectedOrg.description }}
            </span>
          </div>
          <div v-if="selectedOrg && selectedOrg.organization_level" class="mb-3">
            <label class="mt-3">
              {{ $t('activities.organizations.create.level_label') }}
            </label>
            <br>
            <span v-if="selectedOrg">
              <span class="badge badge-info">Level {{ selectedOrg.organization_level }}</span>
            </span>
          </div>
          <div v-if="selectedOrg && selectedOrg.linkedOrganization" class="mb-3">
            <label class="mt-3">
              {{ $t('activities.organizations.table.linked_organization') }}
            </label>
            <br>
            <span v-if="selectedOrg">
              <span>{{ selectedOrg.linkedOrganization.name }}</span>
            </span>
          </div>
        </div>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideCreateOrganizationModal"
                    qid="create-org-cancel-button"
          >
            {{ $t('activities.organizations.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    class="mt-3 ml-3"
                    variant="success"
                    :disabled="buttonDisabled"
                    qid="create-org-submit-button"
          >
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
            </span>
            {{ $t('activities.organizations.create.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>


    <OrganizationViewModal
        :object="selectedOrganization"
        :showModal="viewOrganizationModal"
        @closeViewModal="handleCloseViewOrganizationModal"
    ></OrganizationViewModal>


  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import Avatar from '@/components/Avatar.vue';
  import GuideButton from '@/components/guide/GuideButton.vue';
  import OrganizationViewModal from '@/views/Activities/OrganizationViewModal.vue';
  import UpdateSingleTaskModal from '@/views/Tasks/UpdateSingleTaskModal.vue';

  export default {
    name: 'PoliciesAndProceduresTable',
    data: function() {
      return {
        acForm: {
          name: '',
          provisioning: '',
          re_certification: ''
        },

        // organizations
        organizationTableItems: [],
        organizationLoading: true,
        showOrganizationDescription: false,
        orgItems: [],
        selectedOrg: [],
        viewOrganizationModal: false,
        selectedOrganization: {},
        activity: {},

        error: '',
        loadingItems: false,
        selectedDS: [],
        selectedDSSort: '',
        selectedDSDirection: '',
        acValidationShow: false,
        acTableItems: [],
        acLoading: true,
        dsItems: [],
        updateValidationList: false,
        itemToDelete: {},
        itemObject: {},
        reCertificationOptions: [],
        provisioningOptions: [],
        selectedRC: [],
        selectedP: [],
        validationOptions: [],
        validation: {},
        disabledValidationOptions: [],
        showDSDescription: false,
        buttonDisabled: false,
        selectedDataSubject: {},
        viewDataSubjectModal: false,
        myGuides: [],
        myTasks: []
      }
    },
    props: {
      object: {
        type: Object,
        required: true
      },
      showOnTask: {},
      task: {}
    },
    components: {
      Avatar,
      OrganizationViewModal,
      UpdateSingleTaskModal,
      GuideButton
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      getReloadContent() {
        return this.$store.state.updateContent
      },
      getReloadTasks() {
        return this.$store.state.reloadTasks
      }
    },
    watch: {
      // Watch when client changes in store and trigger api reload
      getClient: function () {

      },
      object: function (value) {
        if (value && value.slug) {
          this.activity = value
          this.loadOrganizations();
          this.loadTasks('update_organization');
        }
      },
      getReloadContent: function () {
        if (!this.showOnTask) {
          if (this.$store.state.updatedObject && this.$store.state.updatedObject.id) {
            if (this.$store.state.updatedObject.id == this.activity.id && this.$store.state.updatedObject.who_am_i == this.activity.who_am_i) {
              this.activity = this.$store.state.updatedObject
            }
          }
          this.reloadContent()
        }
      },
      getReloadTasks: function () {
        if (!this.showOnTask) {
          this.loadTasks('update_organization')
        }
      },
    },
    methods: {
      getTask(code) {
        let existingTask = _.find(this.myTasks, function(object) {
          return object.code == code
        });
        return existingTask
      },
      reloadContent() {
        this.loadOrganizations()
        this.loadTasks('update_organization')
      },
      loadTasks(code) {
        let self = this;
        if (this.getClient.projectModule) {
          piincHttp.get('projects/me/tasks', { params:
            {
              per_page: 1,
              status: 'pending',
              model_type: this.activity.who_am_i,
              model_id: this.activity.id,
              code: code,
              client: this.getClient.slug
            }
          }).then(function(response) {
            self.myTasks = []
            if (response.data.items.length) {
              self.myTasks.push(response.data.items[0])
            }
          }, function() {});
        }
      },
      handleCloseViewOrganizationModal () {
        this.viewOrganizationModal = false;
      },
      openViewOrganizationModal(object) {
        this.selectedOrganization = object
        this.viewOrganizationModal = true
      },
      openToolbar(object) {
        this.$store.commit('storeTaskToolbarState', true);
        this.$store.commit('storeActiveTask', object);
      },
      getGuide(code) {
        let existingGuide = _.find(this.myGuides, function(object) {
          return object.scope == code && object.type == 'Activity' && object.enabled
        });
        return existingGuide
      },
      loadGuides(code) {
        let self = this;
        let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

        piincHttp.get('guides', { params:
          {
            per_page: 1,
            language: 'en',
            type: 'Activity',
            scope: code,
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.myGuides.push(response.data.items[0])
          }
        }, function() {});
      },
      showCreateOrganizationModal() {
        this.loadOrg()
        this.$refs['add-organization'].show()
      },
      hideCreateOrganizationModal() {
        this.error = ''
        this.selectedOrg = [];
        this.showOrganizationDescription = false;
        this.$refs['add-organization'].hide()
      },
      showOrganizationConfirmDeleteModal(object) {
        this.itemToDelete = object
        this.$refs['delete-organization'].show()
      },
      hideOrganizationConfirmDeleteModal() {
        this.itemToDelete = {}
        this.$refs['delete-organization'].hide()
      },
      loadContent() {
        let self = this;
        piincHttp.get('activities/' + this.activity.slug, { params:
          {
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.activity = response.data;
        }, function() {});
      },
      confirmDeleteOrganization(object) {
        let self = this;
        piincHttp.delete('activities/' + this.activity.slug + '/organizations/' + object.id).then(function() {
          self.hideOrganizationConfirmDeleteModal();
          self.loadOrganizations();
          // Trigger toastr message after modal close
          self.$toastr('success', self.$t('activities.organizations.delete.success'))
        }, function() {});
      },
      organizationFormSubmit(evt) {
        this.buttonDisabled = true;
        let formData = new FormData();
        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();

        if (this.selectedOrg && this.selectedOrg.id) {
          formData.append("organization_id", this.selectedOrg.id);
        }

        formData.append("activity", this.activity.slug);

        if (this.selectedOrg && this.selectedOrg.id) {
          piincHttp.post('/activities/' + this.activity.slug + '/organizations', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }).then(function(response) {
            self.$toastr('success', self.$t('activities.organizations.create.success'))
            self.hideCreateOrganizationModal();
            self.loadOrganizations();
            self.loadContent();
            self.buttonDisabled = false;
          }, function(error) {
            if (error.status !== 200) {
              self.buttonDisabled = false;
              self.error = error.data.message;
              if (error.data.errors.organization_id) {
                self.error += ' ' + error.data.errors.organization_id[0]
              }
            }
          })
        } else {
          self.buttonDisabled = false;
          this.error = this.$t('activities.organizations.create.organization_required')
        }

      },
      navigateToTask(object) {
        this.$store.commit('storeTaskToolbarState', true);
        this.$store.commit('storeActiveTask', object);
        this.$router.push({ name: 'activities.view', params: { slug: object.model.slug }, query: { task: object.id }})
      },
      // Table list
      loadOrganizations() {
        let self = this;
        piincHttp.get('activities/' + this.activity.slug + '/organizations', { params:
          {
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.organizationTableItems = response.data;
          self.organizationLoading = false;
        }, function() {});
      },
      loadOrg(query) {
        let self = this;
        piincHttp.get('organizations', { params:
          {
            keyword: query ? query : undefined,
            per_page: 100,
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.orgItems = response.data.items;
          if (self.orgItems && !self.orgItems.length && !query) {
            self.orgItems.push({name: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      },
    },
    created () {
      this.activity = this.object;
      if (this.getClient && this.getClient.slug) {
        this.loadOrg();
      }
      // Table items
      if (this.activity && this.activity.slug) {
        this.loadOrganizations();
        this.loadTasks('update_organization');
      }
      this.loadGuides('update_organization');

    }
  }
</script>
